import { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();
// Helper function to enhance user with permissions
const enhanceUserWithPermissions = (userData) => {
  if (!userData) return userData;
  
  // Check if user has admin privileges (either from current role, original role, or stored flag)
  const hasAdminPrivileges = userData.role === 'admin' || 
                             userData.originalRole === 'admin' || 
                             userData.original_role === 'admin' || 
                             userData.canSwitchRoles === true;
  
  return {
    ...userData,
    canSwitchRoles: hasAdminPrivileges
  };
};
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Set auth token
  const setAuthToken = (token) => {
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      localStorage.setItem('token', token);
    } else {
      delete api.defaults.headers.Authorization;
      localStorage.removeItem('token');
    }
  };

  // Update user function - MODIFIED VERSION
  const updateUser = (userData) => {
    const enhancedUser = enhanceUserWithPermissions(userData);
    setUser(enhancedUser);
    return enhancedUser;
  };

  const needsProfileCompletion = user && 
    (user.age === "" || user.sex === "" || user.user_phone === "");

  // Load user
 // Load user - MODIFIED VERSION
  const loadUser = async () => {
    try {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        setLoading(false);
        return;
      }
      
      setAuthToken(storedToken);
      const res = await api.get('/auth/me');
      
      // Enhance user with permissions before setting
      const enhancedUser = enhanceUserWithPermissions(res.data.user);
      setUser(enhancedUser);
      setIsAuthenticated(true);
    } catch (err) {
      console.error('Error loading user:', err);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Login user
  const login = async (token, userData) => {
    setToken(token);
    setAuthToken(token);
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.removeItem('tableId');
    navigate('../');
  };

  // Logout user
  const logout = () => {
    setToken(null);
    setAuthToken(null);
    setUser(null);
    setIsAuthenticated(false);
    navigate('../');
  };

  const switchRole = async (newRole) => {
    try {
      const res = await api.post('/auth/switch-role', { role: newRole });
      const { token: newToken, user: userData } = res.data;
      
      setAuthToken(newToken);
      
      // IMPORTANT: Preserve the original role and admin privileges
      const enhancedUser = enhanceUserWithPermissions({
        ...userData,
        // Preserve original role if switching away from admin
        originalRole: user?.originalRole || (user?.role === 'admin' ? 'admin' : user?.role),
        // Keep canSwitchRoles flag
        canSwitchRoles: true
      });
      
      setUser(enhancedUser);
      
      return enhancedUser;
    } catch (error) {
      console.error('Role switch error:', error);
      throw error;
    }
  };


  // Check if token exists on app load
  useEffect(() => {
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        loading,
        login,
        logout,
        loadUser,
        updateUser,
        switchRole,
        needsProfileCompletion
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};